import gsap from 'gsap'
import { getAscendantSign } from './astro.js'

const questionPool = {
    growth: [
        "What aspect of my personality should I focus on today to feel more confident?",
        "What internal obstacle is preventing me from taking the next step in my development?",
        "What is my main hidden talent that I am not currently using?",
        "What lesson do I need to learn from the situation currently repeating in my life?"
    ],
    love: [
        "What are my current relationships trying to highlight about my own character?",
        "What energy do I need to bring into my connections to achieve harmony?",
        "What am I projecting into the world, and what kind of people is it attracting?",
        "How can I set healthy boundaries without losing closeness with others?"
    ],
    money: [
        "What professional strategy will bring me the best results this month?",
        "Where is my zone of financial growth that I am currently overlooking?",
        "What quality do I need to develop to take my career to the next level?",
        "Should I take a risk right now, or is it better to preserve my resources?"
    ],
    daily: [
        "What is the main message of the day from the White Rabbit for me right now?",
        "Where will my focus of attention bring the most benefit today?",
        "What old belief should I let go of to live this day more consciously?",
        "What will help me live this day with maximum awareness?"
    ]
}

export function initOracle(supabaseClient, {
    N8N_WEBHOOK,
    CARD_COUNT,
    sounds,
    // cards
    shuffle, revealCard, revealDailyCard, resetCards, getRevealed, setRevealed,
    // rabbit
    speak, showRabbit, hideRabbit,
    // scene (save-result needs renderer/scene/camera)
    renderer, scene, camera,
    // sound helpers
    isMuted, setMuted, stopMouthAnimation,
    // ui
    showMagicAlert, closeMagicAlert, checkAgeConsent,
    updateButtonStates, updateLimitUI, animateCoinFlow,
    // auth (lazy getters — resolved after initAuth)
    getCurrentUser, getUserProfileData, loadUserProfile, signInWithGoogle,
}) {
    // ── LIMIT CHECK ───────────────────────────────────────────────────────────

    function checkLimit() {
        const today        = new Date().toDateString()
        const currentUser  = getCurrentUser()
        const profileData  = getUserProfileData()
        const maxQuestions = currentUser ? 3 : 1

        if (currentUser && profileData) {
            const questionsUsed = parseInt(profileData.questions_total) || 0
            const dailyDone     = profileData.daily_card_done || false
            console.log('Supabase limit check:', questionsUsed, 'out of', maxQuestions)

            const stats = {
                questionCount: questionsUsed,
                limit:         maxQuestions,
                remaining:     Math.max(0, maxQuestions - questionsUsed),
                dailyCardDone: dailyDone
            }
            updateLimitUI(stats)
            return stats
        }

        // Guest mode
        let guestStats
        try { guestStats = JSON.parse(localStorage.getItem('tarot_stats_guest')) } catch { guestStats = null }
        if (!guestStats || guestStats.lastDate !== today) {
            guestStats = { lastDate: today, questionCount: 0 }
        }

        const guestDailyDone = localStorage.getItem('guest_daily_done') === today

        const stats = {
            questionCount: guestStats.questionCount,
            limit:         1,
            remaining:     Math.max(0, 1 - guestStats.questionCount),
            dailyCardDone: guestDailyDone
        }
        updateLimitUI(stats)
        return stats
    }

    // ── RUN ORACLE ────────────────────────────────────────────────────────────

    async function runOracle(isDaily = false) {
        if (getRevealed()) return
        if (!checkAgeConsent(isDaily)) return

        const currentUser = getCurrentUser()
        const stats       = checkLimit()

        if (isDaily) {
            if (stats.dailyCardDone) {
                showMagicAlert("The Oracle has already revealed your card for today. Return when the sun rises again.")
                return
            }
        } else {
            if (stats.questionCount >= stats.limit) {
                const message = currentUser
                    ? "Your 3 daily questions are exhausted. The stars need rest until tomorrow."
                    : "The Oracle grants only one vision to guests. Log in to receive 3 daily predictions!"

                showMagicAlert(message)
                return
            }
        }

        const questionInput = document.getElementById('question')
        let question = questionInput.value.trim()

        if (isDaily) {
            question = "What is my card of the day?"
            questionInput.value = question
        }

        if (!isDaily && (!question || question.length < 5 || !question.includes(' '))) {
            window.currentPrediction = "Please, ask a more detailed question..."
            speak(window.currentPrediction, 'en')
            return
        }

        window.isLimitReachedNow = false
        handleMagicSequence(question, 'en', true, isDaily)
    }

    // ── MAGIC SEQUENCE ────────────────────────────────────────────────────────

    async function handleMagicSequence(question, unused_lang, shouldFetch, isDaily) {
        const currentUser = getCurrentUser()
        const profileData = getUserProfileData()

        if (shouldFetch) {
            if (!currentUser) {
                const stats = checkLimit()
                if (isDaily && stats.dailyCardDone) {
                    showMagicAlert("You've already drawn your daily card.")
                    return
                }
                if (!isDaily && stats.questionCount >= 1) {
                    showMagicAlert("Guest limit reached. Log in for 3 daily questions!")
                    return
                }
            } else {
                if (isDaily && profileData?.daily_card_done) {
                    showMagicAlert("The stars have already spoken today.")
                    return
                }
                if (!isDaily && (profileData?.questions_total >= 3)) {
                    showMagicAlert("Your 3 daily questions are exhausted.")
                    return
                }
            }
        }

        setRevealed(true)
        document.activeElement.blur()
        shuffle()

        const qDisplay = document.getElementById('questionDisplay')
        qDisplay.innerText = question
        gsap.to(qDisplay, { opacity: 0, duration: 0.8 })

        const loaderBar   = document.getElementById('loader-bar')
        const progressBar = document.getElementById('progress')
        loaderBar.style.opacity = '1'
        progressBar.style.width = '10%'

        if (shouldFetch) {
            sounds.shuffle.play()
            sounds.loading.loop = true
            sounds.loading.play().catch(() => {})
            showRabbit()
        }

        const count     = isDaily ? 1 : 3
        const chosenIds = []
        while (chosenIds.length < count) {
            const r = Math.floor(Math.random() * CARD_COUNT)
            if (!chosenIds.includes(r)) chosenIds.push(r)
        }
        window.lastChosenIds = chosenIds

        if (shouldFetch) {
            try {
                const lat = document.getElementById('birth-lat')?.value || null
                const lng = document.getElementById('birth-lng')?.value || null

                let currentAscendant = profileData?.ascendant
                if (!currentAscendant && lat && lng) {
                    const bDate = document.getElementById('birth-date-input')?.value
                    const bTime = document.getElementById('profile-birth-time')?.value
                    currentAscendant = getAscendantSign(bDate, bTime, 2, parseFloat(lat), parseFloat(lng))
                }

                const fp       = await window.fpPromise
                const fpResult = await fp.get()

                const response = await fetch(N8N_WEBHOOK, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({
                        fingerprint:      fpResult.visitorId,
                        userId:           currentUser ? currentUser.id : null,
                        gender:           profileData?.gender || "she",
                        question,
                        cards:            window.lastChosenIds,
                        subscribed:       profileData?.is_subscribed || false,
                        type:             isDaily ? 'daily_card' : 'standard',
                        userName:         profileData?.first_name  || "Mysterious",
                        userLastName:     profileData?.last_name   || "Seeker",
                        userFullName:     profileData?.full_name   || "Mysterious Seeker",
                        personalityArcane: profileData?.personality_arcane || null,
                        soulPath:         profileData?.soul_arcane || null,
                        core_numbers:     profileData?.num_life_path
                            ? `${profileData.num_life_path}-${profileData.num_expression}-${profileData.num_soul_urge}`
                            : "Not calculated",
                        ascendant:  currentAscendant,
                        birthDate:  profileData?.birth_date || document.getElementById('birth-date-input')?.value || null,
                        lat:        lat ? parseFloat(lat) : null,
                        lon:        lng ? parseFloat(lng) : null,
                    })
                })

                const data = await response.json()

                sounds.loading.pause()
                sounds.loading.currentTime = 0
                sounds.shuffle.pause()

                if (data.answer) {
                    window.currentPrediction = data.answer
                    window.detectedLang      = data.language || 'en'
                    speak(window.currentPrediction, window.detectedLang)

                    if (currentUser) {
                        const currentCoins     = profileData?.coins || 0
                        const currentQuestions = profileData?.questions_total || 0

                        const updateData = { coins: currentCoins + 1 }
                        if (isDaily) {
                            updateData.daily_card_done    = true
                            updateData.last_daily_card_at = new Date().toISOString()
                        } else {
                            updateData.questions_total = currentQuestions + 1
                        }

                        const { error: dbError } = await supabaseClient
                            .from('profiles').update(updateData).eq('id', currentUser.id)

                        if (!dbError) {
                            const pd = getUserProfileData()
                            if (pd) {
                                pd.coins = updateData.coins
                                if (isDaily)  pd.daily_card_done    = true
                                else          pd.questions_total    = updateData.questions_total
                            }
                            checkLimit()
                            updateButtonStates()
                            const coinsDisplay = document.getElementById('user-coins-display')
                            if (coinsDisplay) coinsDisplay.innerText = updateData.coins
                        }
                    } else {
                        if (isDaily) {
                            localStorage.setItem('guest_daily_done', new Date().toDateString())
                        } else {
                            const guestStats = JSON.parse(localStorage.getItem('tarot_stats_guest')) || { questionCount: 0 }
                            guestStats.questionCount++
                            guestStats.lastDate = new Date().toDateString()
                            localStorage.setItem('tarot_stats_guest', JSON.stringify(guestStats))
                        }
                        checkLimit()
                        updateButtonStates()
                    }
                } else {
                    window.currentPrediction = "The Oracle is silent. Try again later."
                }

            } catch (error) {
                sounds.loading.pause()
                console.error('Fetch error:', error)
                window.currentPrediction = "Connection to the spirit world lost..."
            }
        }

        setTimeout(() => {
            loaderBar.style.opacity = '0'
            progressBar.style.width = '0%'

            if (isDaily) {
                revealDailyCard(window.lastChosenIds[0], window.currentPrediction)
            } else {
                revealCard()
            }
        }, 1000)
    }

    // ── RESET UI ──────────────────────────────────────────────────────────────

    function resetUI() {
        sounds.click.play()
        hideRabbit()
        const chosenIds = window.lastChosenIds

        setRevealed(false)

        const meaningBlock = document.getElementById('meaning-container')
        const mainUI       = document.getElementById('main-interface')
        const qDisplay     = document.getElementById('questionDisplay')

        gsap.to(qDisplay,     { opacity: 0, duration: 0.3, onComplete: () => { qDisplay.innerText = '' } })
        gsap.to(meaningBlock, { opacity: 0, duration: 0.3, onComplete: () => { meaningBlock.style.display = 'none' } })
        mainUI.style.display = 'flex'
        gsap.to(mainUI, { opacity: 1, duration: 0.5 })
        document.getElementById('ui').classList.remove('ui-hidden')

        resetCards(chosenIds)
        document.getElementById('question').value = ''

        const currentUser = getCurrentUser()
        if (currentUser) {
            animateCoinFlow()
            sounds.coin.play()
            loadUserProfile(currentUser.id).then(() => { checkLimit() })
        } else {
            checkLimit()
        }
    }

    // ── QUESTION MODAL ────────────────────────────────────────────────────────

    window.fillRandomFrom = function(category) {
        sounds.click.play()
        const modal         = document.getElementById('question-modal')
        const listContainer = document.getElementById('questions-list')
        const title         = document.getElementById('modal-category-title')

        if (!modal || !listContainer) { console.error('Modal elements not found!'); return }

        if (category === 'any') {
            const categories = Object.keys(questionPool)
            const randomCat  = categories[Math.floor(Math.random() * categories.length)]
            const questions  = questionPool[randomCat]
            window.selectQuestion(questions[Math.floor(Math.random() * questions.length)])
            return
        }

        listContainer.innerHTML = ''
        title.innerText = category.charAt(0).toUpperCase() + category.slice(1) + " Questions"

        questionPool[category].forEach(q => {
            const btn = document.createElement('div')
            btn.className = 'q-item'
            btn.innerText = q
            btn.onclick   = () => window.selectQuestion(q)
            listContainer.appendChild(btn)
        })

        modal.style.display = 'flex'
    }

    window.selectQuestion = function(question) {
        const input = document.getElementById('question')
        if (input) {
            input.value = question
            input.focus()
            gsap.fromTo(input,
                { boxShadow: "0 0 0px #f3c677" },
                { boxShadow: "0 0 15px #f3c677", duration: 0.3, yoyo: true, repeat: 1 }
            )
        }
        window.closeQuestionModal()
    }

    window.closeQuestionModal = function() {
        sounds.click.play()
        const modal = document.getElementById('question-modal')
        if (modal) modal.style.display = 'none'
    }

    window.addEventListener('click', (event) => {
        const modal = document.getElementById('question-modal')
        if (event.target === modal) window.closeQuestionModal()
    })

    // ── SOUND BUTTONS ─────────────────────────────────────────────────────────

    document.getElementById('toggle-sound').onclick = function() {
        setMuted(!isMuted())
        this.classList.toggle('muted', isMuted())

        if (isMuted()) {
            window.speechSynthesis.cancel()
            stopMouthAnimation()
            const dust = document.getElementById('magic-dust')
            if (dust) dust.classList.remove('active')
        } else {
            if (window.currentPrediction) speak(window.currentPrediction, window.detectedLang || 'en')
        }
    }

    document.getElementById('replay-sound').onclick = () => {
        if (isMuted()) {
            setMuted(false)
            const toggleBtn = document.getElementById('toggle-sound')
            if (toggleBtn) toggleBtn.classList.remove('muted')
        }
        if (window.currentPrediction) speak(window.currentPrediction, window.detectedLang || 'en')
    }

    // ── SAVE RESULT ───────────────────────────────────────────────────────────

    document.getElementById('save-result').addEventListener('click', () => {
        renderer.render(scene, camera)

        html2canvas(document.body, {
            allowTaint:   true,
            useCORS:      true,
            backgroundColor: "#000000",
            scale:        window.devicePixelRatio,
            width:        window.innerWidth,
            height:       window.innerHeight,
            x: 0, y: 0,
            scrollX:      0,
            scrollY:      -window.scrollY,
            windowWidth:  window.innerWidth,
            windowHeight: window.innerHeight,
            onclone: (clonedDoc) => {
                const modal = clonedDoc.getElementById('meaning-container')
                if (modal) {
                    modal.style.position  = 'absolute'
                    modal.style.bottom    = '0'
                    modal.style.transform = 'none'
                }
            }
        }).then(canvas => {
            const link      = document.createElement('a')
            link.download   = 'tarot-reading.png'
            link.href       = canvas.toDataURL('image/png')
            link.click()
            setTimeout(resetUI, 1000)
        })
    })

    // ── BUTTON WIRING ─────────────────────────────────────────────────────────

    document.getElementById('ask').onclick        = () => runOracle(false)
    document.getElementById('daily-card').onclick = () => runOracle(true)
    document.getElementById('close-meaning').onclick = resetUI

    // ── PUBLIC API ────────────────────────────────────────────────────────────

    return { checkLimit, runOracle, resetUI }
}
